import { useFormikContext } from 'formik'
import React from 'react'
import type { LoyaltyRules } from '../../../../../types/loyalty'
import { EditableRuleCard } from './EditableRuleCard'
import { RuleNumberInput } from './RuleNumberInput'
import { RuleRow } from './RuleRow'
import { RuleToggle } from './RuleToggle'

const Icon: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
  </svg>
)

const toNumber = (value: string): number => {
  const n = parseInt(value, 10)
  return Number.isFinite(n) ? n : 0
}

export const RedemptionRulesCard: React.FC = () => {
  const { values, errors, setFieldValue } = useFormikContext<LoyaltyRules>()
  const e = (errors.redemption ?? {}) as Record<string, string>

  return (
    <EditableRuleCard
      title="Redemption Rules"
      description="How accumulated points convert to value at checkout — rate, minimums, and per-order limits"
      icon={<Icon />}
      cardPath="redemption"
      showEnableToggle={false}
    >
      <RuleRow
        name="Redemption rate"
        description="Value of points when redeemed for store credit"
        control={
          <RuleNumberInput
            value={values.redemption.rate}
            onChange={(next) => setFieldValue('redemption.rate', next)}
            unit="pts = $1"
            min={1}
            step={1}
            error={e.rate}
          />
        }
      />
      <RuleRow
        name="Minimum redemption"
        description="Minimum points required for any redemption"
        control={
          <RuleNumberInput
            value={values.redemption.minimum_redemption}
            onChange={(next) =>
              setFieldValue('redemption.minimum_redemption', toNumber(next))
            }
            unit="pts"
            min={0}
            step={50}
            error={e.minimum_redemption}
          />
        }
      />
      <RuleRow
        name="Max redemption per order"
        description="Cap on points redeemable in a single transaction"
        control={
          <RuleNumberInput
            value={values.redemption.max_redemption_per_time}
            onChange={(next) =>
              setFieldValue(
                'redemption.max_redemption_per_time',
                toNumber(next),
              )
            }
            unit="pts"
            min={0}
            step={500}
            error={e.max_redemption_per_time}
          />
        }
      />
      <RuleRow
        name="Allow partial redemption"
        description="Customers can redeem any amount above minimum"
        control={
          <RuleToggle
            checked={values.redemption.allow_partial_redemption}
            onChange={(next) =>
              setFieldValue('redemption.allow_partial_redemption', next)
            }
          />
        }
        isLast
      />
    </EditableRuleCard>
  )
}
